import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Theme types
export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
}

// Language types
export type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Sidebar types
interface SidebarContextType {
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

// Loading types
interface LoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
}

// Theme Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<Theme>('dark');

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Language Context
const translations = {
  en: {
    // Navigation
    'nav.overview': 'Overview',
    'nav.navigation': 'NAVIGATION',
    'nav.cb_universe': 'CB Profile',
    'nav.aggregations': 'Aggregations',
    'nav.portfolio': 'Portfolio',
    'nav.favorites': 'Favorites',
    'nav.simulations': 'Simulations',
    'nav.cb_report': 'CB Report',
    'nav.market_report': 'Market Report',
    'nav.performance': 'Performance',
    'nav.performance_simulation': 'Performance Simulation',
    'nav.admin': 'Admin',
    'nav.admin_teams': 'Teams',
    'nav.admin_users': 'Users',
    'nav.admin_notifications': 'Notifications',
    'nav.admin_data': 'Data',
    'nav.my_data': 'My Data',
    'admin.client_code': 'Client code',
    'admin.include_shared_data': 'Include shared market data',
    'admin.edit_team': 'Edit team',
    'admin.edit_user': 'Edit user',
    'admin.edit': 'Edit',
    'admin.save': 'Save',
    'admin.saving': 'Saving…',
    'admin.shared_data': 'Shared data',
    'admin.shared_and_private': 'Shared + private',
    'admin.private_only': 'Private only',
    'admin.no_client_code': 'No client code',
    'admin.shared_data_enabled_hint': 'This team can see shared (global) market data in addition to its private data.',
    'admin.shared_data_disabled_hint': 'This team only sees its private client data. Enable to also grant access to shared market data.',
    'admin.assign_to_team': 'Assign to team',
    'admin.remove_user_from_team': 'Remove from team',
    'admin.remove': 'Remove',
    'nav.admin_leads': 'Leads',
    'nav.admin_plans': 'Plans',
    'nav.admin_ai': 'AI Instructions',
    'nav.admin_reviews': 'Analyst Reviews',
    'nav.analyst_reviews': 'Review Requests',
    'nav.logout': 'Logout',

    'admin.title': 'Admin',
    'admin.description': 'Manage teams, users, and data exports',
    'admin.teams': 'Teams',
    'admin.users': 'Users',
    'admin.data_import': 'Data Import',
    'admin.import_cbhist': 'Import cbhist (JSON)',
    'admin.import_cbhist_hint': 'Upload cbhist.json to replace bond daily data. Bonds must exist in DB.',
    'admin.create_team': 'Create team',
    'admin.create_user': 'Create user',
    'admin.team_name': 'Team name',
    'admin.team_description': 'Description',
    'admin.user_email': 'Email',
    'admin.user_name': 'Name',
    'admin.user_role': 'Role',
    'admin.user_team': 'Team',
    'admin.user_active': 'Active',
    'admin.no_team': '—',
    'admin.login_as': 'Login as this user',
    'admin.back_to_admin': 'Back to admin',
    'admin.send_notification': 'Send notification',
    'admin.notifications_tab': 'Notifications',
    'admin.to_all': 'All users',
    'admin.to_user': 'To user',
    'admin.team_users': 'Users in team',
    'admin.loading_teams': 'Loading teams...',
    'admin.loading_users': 'Loading users...',
    'admin.no_users_in_team': 'No users in this team.',
    'admin.close': 'Close',
    'admin.notification_title': 'Notification title',
    'admin.message_optional': 'Message (optional)',
    'admin.password': 'Password',
    'admin.viewer': 'Viewer',
    'admin.analyst': 'Analyst',
    'admin.delete_team_confirm': 'Delete this team?',
    'admin.imported_count': 'Imported',
    'admin.skipped_count': 'Skipped (no matching bond)',
    'admin.importing': 'Importing...',
    'admin.import': 'Import',
    'admin.notification_sent': 'Notification(s) sent.',
    'admin.delete': 'Delete',
    // Plans
    'admin.plans': 'Plans',
    'admin.plans_title': 'Plans',
    'admin.create_plan': 'Create Plan',
    'admin.plan_name': 'Plan name',
    'admin.plan_description': 'Description',
    'admin.plan_price': 'Price',
    'admin.plan_period_days': 'Days',
    'admin.allowed_tabs': 'Allowed Tabs',
    'admin.assign_plan': 'Assign',
    'admin.assign_plan_to_user': 'Assign Plan to User',
    'admin.select_user': 'Select User',
    'admin.select_plan': 'Select Plan',
    'admin.existing_plans': 'Existing Plans',
    'admin.loading': 'Loading...',
    'admin.loading_plans': 'Loading...',
    'admin.plan_assigned': 'Plan assigned!',
    'admin.price_period_tabs': 'Price: {price} | Period: {days} days | Tabs: {tabs}',
    // AI Instructions
    'admin.ai_instructions': 'AI Instructions',
    'admin.ai_instructions_title': 'AI Instructions',
    'admin.set_ai_instruction': 'Set AI Instruction for Page',
    'admin.ai_instruction_hint': 'Define behavior instructions for the AI assistant on each dashboard page.',
    'admin.save_instruction': 'Save Instruction',
    'admin.current_instructions': 'Current Instructions',
    'admin.no_instructions': 'No instructions set yet.',
    'admin.instruction_saved': 'Instruction saved!',
    'admin.ai_placeholder': 'AI instructions for {page} page...',
    'admin.updated': 'Updated',
    'admin.edit_instruction': 'Edit',
    'admin.delete_instruction': 'Delete',
    'admin.instruction_deleted': 'Instruction deleted.',
    'admin.delete_instruction_confirm': 'Delete this AI instruction?',
    // Analyst Reviews
    'admin.analyst_reviews': 'Analyst Reviews',
    'admin.reviews_title': 'All Analyst Reviews',
    'admin.no_reviews_yet': 'No reviews yet.',
    'admin.loading_reviews': 'Loading...',
    'admin.select_analyst': 'Select analyst',
    'admin.assign': 'Assign',
    'admin.assigning': 'Assigning...',
    'admin.user': 'User',
    'admin.analyst_label': 'Analyst',
    'admin.page': 'Page',
    'admin.unassigned': 'Unassigned',
    // Data (admin)
    'admin.data_title': 'Data',
    'admin.data_description': 'View and edit data',
    'walkthrough.admin_data_label': 'Data',
    'walkthrough.admin_data_description': 'View and edit daily bond data, bonds, projections, and issuers. Create, edit or delete rows from each tab.',
    'walkthrough.leads_label': 'Leads',
    'walkthrough.leads_description': 'View newsletter signups and demo requests from the landing page. Export or manage marketing leads.',
    'walkthrough.overview_label': 'Overview',
    'walkthrough.overview_description': 'Your main dashboard with real-time insights into the convertible bonds market, KPIs, and key metrics.',
    'walkthrough.universe_label': 'CB Universe',
    'walkthrough.universe_description': 'Browse and filter the full convertible bonds universe with detailed bond data and screening tools.',
    'walkthrough.aggregations_label': 'Aggregations',
    'walkthrough.aggregations_description': 'Cross-filter and aggregate market data across dimensions like sector, rating, and issuer.',
    'walkthrough.performance_label': 'Performance',
    'walkthrough.performance_description': 'Performance attribution and analytics for your portfolio and selected bonds.',
    'walkthrough.teams_label': 'Teams',
    'walkthrough.teams_description': 'Create and manage teams. View which users belong to each team and delete teams you no longer need.',
    'walkthrough.users_label': 'Users',
    'walkthrough.users_description': 'Create new users, set their role (viewer, analyst, admin) and team. Impersonate a user to see the app as they do.',
    'walkthrough.notifications_label': 'Notifications',
    'walkthrough.notifications_description': 'Send in-app notifications to all users or to a specific user. Use for announcements or alerts.',
    'walkthrough.plans_label': 'Plans',
    'walkthrough.plans_description': 'Create subscription plans with name, price, period and allowed dashboard tabs. Assign plans to users to control their access.',
    'walkthrough.ai_label': 'AI Instructions',
    'walkthrough.ai_description': 'Set per-page instructions for the AI assistant. Define how the assistant should behave on Overview, Universe, Aggregations and Performance pages.',
    'walkthrough.reviews_label': 'Analyst Reviews',
    'walkthrough.reviews_description': 'View all analyst review requests. Assign pending reviews to analysts so they can complete the analysis.',
    'walkthrough.skip': 'Skip',
    'walkthrough.back': 'Back',
    'walkthrough.next': 'Next',
    'walkthrough.done': 'Done',
    'admin.data_tab_daily': 'Daily data',
    'admin.data_tab_bonds': 'Bonds',
    'admin.data_tab_projections': 'Projections',
    'admin.data_tab_issuers': 'Issuers',
    'admin.data_no_rows': 'No rows.',
    'admin.data_bond_name': 'Bond',
    'admin.data_bond_code': 'Bond code',
    'admin.data_search': 'Search',
    'admin.data_apply_filters': 'Apply',
    'admin.data_clear_filters': 'Clear',
    'admin.data_date': 'Date',
    'admin.data_issuer_name': 'Issuer',
    'admin.data_currency': 'Currency',
    'admin.data_maturity': 'Maturity',
    'admin.data_ex_date': 'Ex date',
    'admin.data_country': 'Country',
    'admin.data_issuer_sector': 'Issuer sector',
    'admin.data_delete_confirm': 'Delete this row?',
    'admin.data_showing': 'Showing',
    'admin.data_of': 'of',
    'admin.data_prev': 'Previous',
    'admin.data_next': 'Next',
    'admin.data_page': 'Page',
    'admin.data_per_page': 'Per page',
    'admin.data_create': 'Create',
    'admin.data_required_fields': 'Please fill required fields.',
    'admin.data_bulk_import': 'Bulk Import',
    'admin.data_bulk_delete': 'Bulk Delete',
    'admin.data_bulk_delete_count': 'Bulk Delete ({count})',
    'admin.data_create_daily': 'Create Daily Data',
    'admin.data_create_bond': 'Create Bond',
    'admin.data_create_projection': 'Create Dividend Projection',
    'admin.data_create_issuer': 'Create Issuer',
    'admin.data_import_daily': 'Bulk Import Daily Data (Excel / CSV)',
    'admin.data_import_bonds': 'Bulk Import Bonds (Excel / CSV)',
    'admin.data_import_projections': 'Bulk Import Projections (Excel / CSV)',
    'admin.data_import_issuers': 'Bulk Import Issuers (Excel / CSV)',
    'admin.data_excel_format': 'Excel or CSV format — first row must be headers. Required columns:',
    'admin.data_excel_optional': 'Optional columns:',
    'admin.data_choose_excel': 'Choose Excel or CSV file',
    'admin.data_download_excel_template': 'Download Excel template',
    'admin.data_download_csv_template': 'Download CSV template',
    'admin.data_download_excel': 'Download Excel',
    'admin.data_download_csv': 'Download CSV',
    'admin.data_exporting': 'Exporting…',
    'admin.data_export_empty': 'No data to export for the current filters.',
    'admin.data_importing': 'Importing…',
    'admin.data_creating': 'Creating…',
    'admin.data_save_changes': 'Save Changes',
    'admin.data_clear': 'Clear',
    'admin.data_view': 'View',
    'admin.data_del': 'Del',
    'admin.data_rows': 'rows',
    'admin.data_bonds_count': 'bonds',
    'admin.data_projections_count': 'projections',
    'admin.data_issuers_count': 'issuers',
    'admin.data_bond_code_required': 'Bond code and date are required',
    'admin.data_no_valid_rows_daily': 'No valid rows found. Ensure bondCode and date columns exist.',
    'admin.data_select_one_row': 'Select at least one row',
    'admin.data_select_one_bond': 'Select at least one bond',
    'admin.data_select_one_projection': 'Select at least one projection',
    'admin.data_select_one_issuer': 'Select at least one issuer',
    'admin.data_delete_daily_confirm': 'Delete {count} selected row(s)? This cannot be undone.',
    'admin.data_delete_bonds_confirm': 'Delete {count} selected bond(s) and their daily data? This cannot be undone.',
    'admin.data_delete_projections_confirm': 'Delete {count} selected projection(s)?',
    'admin.data_delete_issuers_confirm': 'Delete {count} selected issuer(s)? Bonds referencing them will need to be updated.',
    'admin.data_deleted_count': 'Deleted {count} rows',
    'admin.data_deleted_bonds': 'Deleted {count} bonds',
    'admin.data_deleted_projections': 'Deleted {count} projections',
    'admin.data_deleted_issuers': 'Deleted {count} issuers',
    'admin.data_imported_count': 'Imported {count} rows',
    'admin.data_imported_bonds': 'Imported {count} bonds',
    'admin.data_imported_projections': 'Imported {count} projections',
    'admin.data_imported_issuers': 'Imported {count} issuers',
    'admin.data_no_valid_rows_bonds': 'No valid rows. Ensure all required columns exist.',
    'admin.data_no_valid_rows_projections': 'No valid rows. Ensure bondCode, exDate and amount columns exist.',
    'admin.data_no_valid_rows_issuers': 'No valid rows. Ensure name and country columns exist.',
    'admin.data_bond_exdate_amount_required': 'Bond code, ex-date and amount are required',
    'admin.data_name_country_required': 'Name and country are required',
    // Leads (newsletter & demo requests)
    'admin.leads_title': 'Leads',
    'admin.leads_description': 'Newsletter signups and demo requests from the landing page',
    'admin.leads_tab_newsletter': 'Newsletter signups',
    'admin.leads_tab_demo': 'Demo requests',
    'admin.leads_newsletter_count': 'signups',
    'admin.leads_demo_count': 'requests',
    'admin.leads_email': 'Email',
    'admin.leads_signed_up_at': 'Signed up',
    'admin.leads_name': 'Name',
    'admin.leads_company': 'Company',
    'admin.leads_phone': 'Phone',
    'admin.leads_requested_at': 'Requested',
    'admin.edit': 'Edit',
    'admin.save': 'Save',
    'admin.cancel': 'Cancel',
    'admin.actions': 'Actions',

    // Account
    'account.messages': 'Messages',
    'account.settings': 'Settings',
    'account.help': 'Help',

    // Login
    'login.enter_details': 'Please enter your details.',
    'login.email': 'Email',
    'login.email_placeholder': 'Enter your email',
    'login.password': 'Password',
    'login.log_in': 'Log in',
    'login.signing_in': 'Signing in...',
    'login.invalid_credentials': 'Invalid email or password',
    'login.error_generic': 'An error occurred. Please try again.',

    'errors.network_issue': 'There was a network issue. Check your connection to the internet and try again.',
    'errors.generic': 'Something went wrong. Please try again.',
    
    // Dashboard Pages
    'dashboard.overview': 'Dashboard Overview',
    'dashboard.overview_desc': 'Real-time insights into the convertible bonds market',
    'dashboard.analyst_desc': 'Manage assigned reviews, view as requester, and submit analysis reports',
    'dashboard.universe': 'Convertible Bonds Universe',
    'dashboard.universe_desc': 'bonds',
    'dashboard.aggregations': 'Aggregations',
    'dashboard.aggregations_desc': 'Analyze market across multiple dimensions',
    'dashboard.portfolio': 'Portfolio Analysis',
    'dashboard.portfolio_desc': 'bonds selected • Total market cap:',
    'dashboard.instrument_desc': 'ISIN:',
    
    // KPIs
    'kpi.total_convertible_bonds': 'Total Convertible Bonds',
    'kpi.total_market_cap': 'Total Market Cap',
    'kpi.average_yield': 'Average Yield',
    'kpi.average_1d_change': 'Average 1D Change',
    'kpi.ytm': 'YTM',
    'kpi.vs_yesterday': 'vs yesterday',
    'kpi.price': 'Price',
    'kpi.fair_value': 'Fair Value',
    'kpi.ytm_yield': 'YTM (Yield)',
    'kpi.coupon': 'Coupon',
    'kpi.conversion_price': 'Conversion Price',
    'kpi.parity': 'Parity',
    'kpi.delta': 'Delta',
    'kpi.theta': 'Theta',
    'kpi.total_bonds': 'Total Bonds',
    'kpi.weighted_delta': 'Weighted Delta',
    'kpi.weighted_ytm': 'Weighted YTM',
    'kpi.1m_performance': '1M Performance',
    'kpi.avg_performance_1m': 'Avg Performance 1M',
    'kpi.performance_1m': 'Performance 1M',
    'kpi.portfolio_value': 'Market Value',
    'kpi.daily_pnl': 'Daily P&L',
    'kpi.portfolio_delta': 'Delta',
    'kpi.avg_ytm': 'YTM',
    'kpi.avg_implied_vol': 'Implied Vol',
    'kpi.portfolio_vega': 'Vega',
    'kpi.portfolio_gamma': 'Gamma',
    'kpi.avg_prime': 'Premium',
    'kpi.avg_duration': 'Duration',
    'kpi.avg_credit_spread': 'Credit Spread',
    'kpi.subtitle_eur': 'EUR',
    'kpi.subtitle_vs_yesterday': 'vs Yesterday',
    'kpi.subtitle_equity_exposure': 'Equity Exposure',
    'kpi.subtitle_yield_to_maturity': 'Yield to Maturity',
    'kpi.subtitle_balanced_profile': 'Balanced Profile',
    'kpi.subtitle_vol_sensitivity': 'Vol Sensitivity',
    'kpi.subtitle_convexity': 'Convexity',
    'kpi.subtitle_conversion_premium': 'Conversion Premium',
    'kpi.subtitle_interest_rate_risk': 'Interest Rate Risk',
    'kpi.subtitle_bond_profile': 'Bond Profile',
    
    // Charts & Widgets
    'widget.market_index_performance': 'Market Index Performance',
    'widget.sector_breakdown': 'Sector Breakdown',
    'widget.credit_rating_breakdown': 'Credit Rating Breakdown',
    'widget.maturity_breakdown': 'Maturity Breakdown',
    'widget.profile_breakdown': 'Profile Breakdown',
    'widget.performance_history': 'Performance History (30 Days)',
    'widget.sector_allocation': 'Sector Allocation',
    'widget.rating_distribution': 'Rating Distribution',
    'widget.credit_rating_distribution': 'Credit Rating Distribution',
    'widget.historical_prices': 'Historical Prices & Greeks',
    'widget.bond_characteristics': 'Bond Characteristics',
    'widget.last_30_days': 'Last 30 days performance trend',
    'widget.top_worst_performers': 'Top & Worst Performers',
    'widget.top_3': 'Top 3',
    'widget.worst_3': 'Worst 3',
    'widget.cb_performance': 'CB Performance',
    'widget.equity_performance': 'Equity Performance',
    'widget.country_distribution': 'Country Distribution',
    'widget.cb_strike_analysis': 'CB Strike vs CB Price',
    'widget.cb_strike_description': 'Relationship between CB Strike and Convertible Bond Price, colored by CB Profile',
    'widget.inflows_outflows': 'Inflows / Outflows',
    'widget.inflows': 'Inflows',
    'widget.outflows': 'Outflows',
    'flows.added': 'Added',
    'flows.removed': 'Removed',

    // Legend items
    'legend.cb_index': 'CB Index',
    'legend.equity_index': 'Equity Index',
    'legend.delta_neutral': 'Delta Neutral',
    
    // Table Headers
    'table.isin': 'ISIN',
    'table.issuer': 'Issuer',
    'table.sector': 'Sector',
    'table.currency': 'Currency',
    'table.coupon': 'Coupon',
    'table.price': 'Price',
    'table.delta': 'Delta',
    'table.rating': 'Rating',
    'table.maturity': 'Maturity',
    'table.perf_1m': 'Perf 1M',
    
    // Buttons
    'button.export_csv': 'Export to CSV',
    'button.back_to_universe': 'Back to Universe',
    'button.save_inputs': 'Save Custom Inputs',
    
    // Filter Labels
    'filter.search': 'Search by ISIN, issuer, or country...',
    'filter.sector': 'Sector',
    'filter.country': 'Country',
    'filter.rating': 'Credit Rating',
    'filter.currency': 'Currency',
    
    // Dimension Labels
    'dimension.primary': 'Primary Dimension',
    'dimension.secondary': 'Secondary Dimension',
    'dimension.secondary_cross_filter': 'Secondary Dimension (Cross-Filter)',
    'dimension.sector': 'Sector',
    'dimension.rating': 'Credit Rating',
    'dimension.size': 'Market Cap Size',
    'dimension.profile': 'Bond Profile',
    'dimension.maturity': 'Maturity Bucket',
    
    // Aggregations Page
    'aggregations.universe_weighted_greeks': 'Greeks',
    'aggregations.valuation_metrics': 'Valuation Metrics',
    'aggregations.premium_adjusted': 'Premium / Adjusted Premium',
    'aggregations.yield_to_maturity': 'Yield to Maturity',
    'aggregations.implied_historical_vol': 'Implied Vol vs Historical Vol (VI & VH)',
    'aggregations.credit_spread': 'Credit Spread & Implied Spread',
    'aggregations.historical_distribution': 'Historical Distribution',
    'aggregations.breakdown_by': 'Breakdown by',
    'aggregations.detailed_breakdown': 'Detailed Breakdown',
    'aggregations.summary_table': 'Summary Table',
    'aggregations.cross_filter_analysis': 'Cross-Filter Analysis',
    'aggregations.cb_market_cap': 'CB Market Cap',
    'aggregations.equity_market_cap': 'Equity Market Cap',
    'aggregations.avg_prime': 'Premium %',
    'aggregations.avg_parity': 'Parity %',
    'aggregations.avg_ytm': 'YTM',
    'aggregations.bond_floor': 'Bond Floor %',
    'aggregations.no_cross_filter': 'No cross-filter available for',
    'aggregations.showing': 'Showing',
    'aggregations.breakdown_with_cross_filter': 'breakdown with cross-filter by',
    'aggregations.count': 'Count',
    'aggregations.market_cap': 'Market Cap',
    'aggregations.avg_delta': 'Delta',
    'aggregations.current_distribution': 'Current Distribution:',
    
    // Performance Attribution Page
    'performance.title': 'Performance & Attribution',
    'performance.description': 'Performance analysis',
    'performance.select_period': 'Select Period',
    'performance.export_data': 'Export Data',
    'performance.period': 'Period',
    'performance.universe_performance': 'Universe Performance',
    'performance.cb_performance': 'CB Performance',
    'performance.equity_performance': 'Equity Performance',
    'performance.delta_neutral': 'Delta Neutral',
    'performance.cb_pnl': 'Convertible Bond P&L',
    'performance.equity_contribution': 'Underlying equity contribution',
    'performance.hedged_performance': 'Hedged performance',
    'performance.performance_trend': 'Performance Trend',
    'performance.attribution': 'Performance Attribution',
    'performance.attribution_description': 'Breakdown by contribution source: Share, Credit, Carry, Rate, Valuation, FX',
    'performance.total': 'Total',
    'performance.share': 'Equity Contribution',
    'performance.credit_spread': 'Credit Spread',
    'performance.carry': 'Carry',
    'performance.rate': 'Rate',
    'performance.valuation': 'Valuation',
    'performance.fx': 'FX',
    'performance.contribution': 'Contribution',
    'performance.by_filter': 'Performance by Filter',
    'performance.weight': 'Weight',
    'performance.delta_gamma': 'Equity', 
    'performance.credit': 'Spread',
    'performance.spread': 'Spread',
    'performance.total_contrib': 'Total Contrib',
    'performance.perf_abs': 'Perf Abs',
    'performance.perf_abs_cb': 'Perf Abs CB',
    'performance.perf_abs_equity': 'Perf Abs Equity',
    'performance.equity_perf_abs': 'Equity Perf Abs (oc)',
    'performance.by': 'by',
    'performance.bubble_description': 'Each bubble represents a category. X-axis: CB Performance (Abs), Y-axis: Equity Performance (Abs).',
    'performance.top_worst_contributors': 'Top & Worst Contributors',
    'performance.top_10': 'Top 10 Contributors',
    'performance.worst_10': 'Worst 10 Contributors',
    'performance.absolute_performance': 'Abs Perf',
    'performance.cb_vs_equity': 'CB vs Equity Performance',
    'performance.scatter_description': 'Each point represents a convertible bond. X-axis: Equity contribution, Y-axis: CB performance. Size: Outstanding amount.',
    'performance.equity_contrib': 'Equity Contribution (%)',
    'performance.cb_perf': 'CB Performance (%)',
    'performance.from': 'From',
    'performance.to': 'To',
    'performance.select_start': 'Select start date',
    'performance.select_end': 'Select end date',
    
    // Instrument Page
    'instrument.back_to_universe': 'Back to Universe',
    'instrument.performance_overview': 'Performance Overview',
    'instrument.latest_data': 'Latest Data',
    'instrument.live_values': 'Live Values',
    'instrument.cb_price': 'CB Price',
    'instrument.stock_price': 'Stock Price',
    'instrument.spread_input': 'Spread (Input)',
    'instrument.implied_spread': 'Implied Spread',
    'instrument.implied_vol': 'VI (Implied Vol)',
    'instrument.historical_vol': 'VH (Historical Vol)',
    'instrument.volatility_analysis': 'Volatility Analysis',
    'instrument.credit_analysis': 'Credit Analysis',
    'instrument.vega_high': '(High)',
    'instrument.vega_low': '(Low)',
    'instrument.vol_spread': 'Vol Spread (VI - VH)',
    'instrument.avg_spread_vol': 'Average Spread Vol',
    'instrument.std_deviation': 'Standard Deviation',
    'instrument.spread_to_average': 'Spread to Average',
    'instrument.zscore': 'Z-Score',
    'instrument.downside_risk': 'Downside Risk',
    'instrument.spread_differential': 'Spread Differential',
    'instrument.current_yield': 'Current Yield',
    'instrument.credit_sensitivity': 'Credit Sensitivity',
    'instrument.relative_valuation': 'Relative Valuation',
    'instrument.static_information': 'Static Information',
    'instrument.issue_date': 'Issue Date',
    'instrument.conversion_ratio': 'Conversion Ratio',
    'instrument.underlying': 'Underlying',
    'instrument.outstanding_amount': 'Outstanding Amount',
    'instrument.richness_cheapness': 'Richness / Cheapness Summary',
    'instrument.observation': 'Observation',
    'instrument.financial_indicators': 'Financial Indicators',
    'instrument.historical_analysis': 'Historical Analysis',
    'instrument.cb_vs_underlying': 'CB Price vs Underlying',
    'instrument.custom_inputs': 'Custom Inputs',
    'instrument.custom_inputs_description': 'Override default values for personalized analysis. Values are saved locally.',
    'instrument.save_custom': 'Save Custom Inputs',
    'instrument.repo': 'Repo (%)',
    'instrument.underpriced': 'Underpriced',
    'instrument.overpriced': 'Overpriced',
    'instrument.fair': 'Fair',
    
    // Universe Page
    'universe.select_bond': 'Select a Convertible Bond',
    'universe.select_placeholder': 'Search by issuer or ISIN...',
    'universe.chart_category': 'Chart Category',
    'universe.chart_type': 'Chart Type',
    'universe.performance_period': 'Performance Period',
    'universe.key_metrics': 'Key Metrics',
    'universe.performance': 'Performance',
    'universe.greeks': 'Greeks',
    'universe.pricing_valuation': 'Pricing & Valuation',
    'universe.valuation_metrics': 'Valuation Metrics',
    'universe.days': 'days',
    'universe.actual_days': 'Actual Days',
    'universe.custom_inputs': 'Custom Inputs',
    'universe.cb_price': 'CB Price',
    'universe.volatility': 'Volatility',
    'universe.stock': 'Stock',
    'universe.apply': 'Apply',
    'universe.reset': 'Reset',
    'universe.issue_characteristics': 'Issue Characteristics',
    'universe.nominal': 'Nominal',
    'universe.conversion_ratio': 'Conversion Ratio',
    'universe.conversion_price': 'Conversion Price',
    'universe.issue_price': 'Issue Price',
    'universe.coupon': 'Coupon',
    'universe.issue_date': 'Issue Date',
    'universe.maturity_date': 'Maturity Date',
    'universe.amount_issued': 'Amount Issued',
    'universe.currency': 'Currency',
    'universe.rating': 'Rating',
    'universe.underlying': 'Underlying',
    'universe.performance_attribution': 'Performance Attribution',
    'universe.start_date': 'Start Date',
    'universe.end_date': 'End Date',
    'universe.share_contrib': 'Share Contrib',
    'universe.credit_spread_contrib': 'Credit Spread Contrib',
    'universe.carry_contrib': 'Carry Contrib',
    'universe.rate_contrib': 'Rate Contrib',
    'universe.valuation': 'Valuation',
    'universe.fx_contrib': 'FX Contrib',
    'universe.select_date_range': 'Select a date range to see attribution',
    'universe.ratchet_takeover': 'Ratchet Clause & Takeover Premium',
    'universe.change_of_control': 'Change of Control',
    'universe.takeover_simulation': 'Takeover Simulation',
    'universe.takeover_premium': 'Takeover Premium (%)',
    'universe.bid_date': 'Bid Date',
    'universe.results': 'Results',
    'universe.current_cp': 'Current CP',
    'universe.new_cp': 'New CP',
    'universe.current_cr': 'Current CR',
    'universe.new_cr': 'New CR',
    'universe.bond_performance': 'Bond Performance',
    'universe.latest_valuation': 'Latest Valuation & KPIs',
    'universe.credit_metrics': 'Credit Metrics',
    'universe.current_yield': 'Current Yield',
    'universe.credit_sens': 'Credit Sens.',
    'universe.volatility_sensitivity': 'Volatility & Sensitivity',
    'universe.pricing': 'Pricing',
    'universe.fair_value': 'Fair Value',
    'universe.option_value': 'Option Value',
    'universe.premium': 'Premium',
    'universe.adj_premium': 'Adj. Premium',
    'universe.parity': 'Parity',
    'universe.historical_analysis': 'Historical Analysis',
    'universe.premium_percent': 'Premium %',
    'universe.adj_premium_percent': 'Adjusted Premium %',
    'universe.implied_spread_bp': 'Implied Spread (bp)',
    'universe.credit_spread_bp': 'Credit Spread (bp)',
    
    // Common
    'common.issuer': 'Issuer',
    'common.sector': 'Sector',
    'common.country': 'Country',
    'common.rating': 'Rating',
    'common.maturity': 'Maturity',
    'common.type': 'Type',
    'common.profile': 'Profile',
    'common.size': 'Size',
    'common.bond_not_found': 'Bond not found',
    'common.no_data': 'No data available',
    'common.loading': 'Loading...',
    'common.percentage': '%',
    'common.basis_points': 'bp',
    
    // Other
    'currency.us_dollar': 'EUR',
    'search.placeholder': 'Search by ISIN, issuer, or country...',
    'portfolio.select_bonds': 'Select bonds to include in your portfolio analysis',
    'chart.primary_dimension': 'Primary Dimension Chart',
    'chart.secondary_dimension': 'Secondary Dimension Chart',
    'dark_mode': 'Dark Mode',
    'light_mode': 'Light Mode',
    'of': 'of',
    'tracking': 'Tracking',
    
    // Signals Analysis
    'signals.impliedVolatilityAnalysis': 'Implied Volatility Analysis',
    'signals.impliedSpreadAnalysis': 'Implied Spread Analysis',
    'signals.creditSpreadGapAnalysis': 'Credit-Driven Spread Gap Analysis',
    'signals.lookbackPeriod': 'Lookback Period',
    'signals.impliedVol': 'Implied Vol',
    'signals.ivVariation': 'IV Variation',
    'signals.actualDays': 'Actual Days',
    'signals.creditSpread': 'Credit Spread',
    'signals.impliedSpread': 'Implied Spread',
    'signals.spreadVariation': 'Spread Var',
    'signals.valuation': 'Valuation',
    'signals.noCBsMeetingCriteria': 'No CBs meeting the criteria for this period',
    'signals.overpriced': 'Overpriced',
    'signals.underpriced': 'Underpriced',
    'signals.expensive': 'Expensive',
    'signals.fair_value': 'Fair Value',
    'signals.relative_situation': 'Relative situation',
    
    // Overview Page Tabs
    'tabs.overview': 'Overview',
    'tabs.performance': 'Performance',
    'tabs.signals_analysis': 'Signals Analysis',
    
    // Performance Cards
    'performance.universe_1d': 'Universe Performance (1D)',
    'performance.equity_1d': 'Equity Performance (1D)',
    'performance.delta_neutral_1d': 'Delta-Neutral Performance (1D)',
    
    // Size Breakdown
    'widget.size_breakdown': 'Size Breakdown (by Equity Market Cap)',
    
    // Size Labels
    'size.large_cap': 'Large Cap',
    'size.mid_cap': 'Mid Cap',
    'size.small_cap': 'Small Cap',
    
    // Profile Labels
    'profile.balanced': 'Balanced',
    'profile.bond': 'Bond',
    'profile.equity': 'Equity',
    'profile.high_yield': 'High Yield',
    'profile.distressed': 'Distressed',
    
    // Period Labels
    'period.1d': '1D',
    'period.mtd': 'MTD',
    'period.3mtd': '3MTD',
    'period.ytd': 'YTD',
    'period.vol_sensitive_bound': 'Vol-Sensitive Bound',
    
    // Implied Volatility Widget
    'widget.implied_volatility_variation': 'Implied Volatility Variation – Vol-Sensitive Regime',
    'widget.top_5_implied_vol': 'Top 5 Implied Vol ↑',
    'widget.flop_5_implied_vol': 'Flop 5 Implied Vol ↓',
    
    // Implied Spread Widget
    'widget.implied_spread_variation': 'Implied Spread Variation – Credit-Sensitive Universe',
    'widget.top_5_spread_widening': 'Top 5 Spread Widening ↑',
    'widget.bottom_5_spread_tightening': 'Bottom 5 Spread Tightening ↓',
    
    // Table Headers for Rankings
    'table.delta_iv': 'ΔIV',
    'table.delta_spr': 'ΔSpr',
    'table.vega': 'Vega',
    'table.profile': 'Profile',

    // AI Assistant
    'ai.disclaimer': 'AI is not perfect and may make mistakes. This is an analyst tool only — not financial advice.',
    'ai.assistant_title': 'ConvPilot Assistant',
    'ai.online': 'Online',
    'ai.live_data': 'Live data',
    'ai.placeholder': 'Ask about your portfolio, bonds, metrics...',
    'ai.welcome': 'Hello! I\'m your ConvPilot AI assistant. I have access to your current dashboard data. Ask me anything about your portfolio, bond metrics, or market analysis!',
    'ai.thinking': 'Thinking...',
    'ai.error': 'Sorry, I encountered an error. Please try again.',

    // Header & modals
    'header.profile': 'Profile',
    'header.logout': 'Logout',
    'header.back_to_analyst': 'Back to analyst',
    'header.user': 'User',
    'modal.close': 'Close',
    'modal.cancel': 'Cancel',
    'modal.logout_confirm_title': 'Log out?',
    'modal.logout_confirm_message': 'Are you sure you want to log out?',
    'modal.analyst_reviews': 'Analyst Reviews',
    'modal.request_analyst_review': 'Request Analyst Review',
    'modal.my_reviews': 'My Reviews',
    'modal.review_requests': 'Review Requests',
    'modal.new_request': 'New Request',
    'modal.write_analysis': 'Write Analysis',
    'modal.report_title': 'Report title...',
    'modal.write_analysis_placeholder': 'Write your analysis (Markdown supported: **bold**, *italic*, tables, lists)...',
    'modal.preview_live_hint': 'Preview updates as you type (right panel).',
    'modal.submit_analysis': 'Submit Analysis Report',
    'modal.submitting': 'Submitting...',
    'modal.assign_to_me': 'Assign to Me',
    'modal.view_as_user': 'View as user',
    'modal.back_to_list': '← Back to list',
    'modal.review_request': 'Review Request',
    'modal.analysis_reports': 'Analysis Reports',
    'modal.no_reviews': 'No reviews yet.',
    'modal.no_reviews_hint': 'Submit a request to get started.',
    'modal.loading_reviews': 'Loading reviews...',
    'modal.describe_requirements': 'Describe what you\'d like the analyst to review. Be specific about your requirements and the data you\'d like analyzed.',
    'modal.submit_review_request': 'Submit Review Request',
    'modal.pending': 'Pending',
    'modal.in_progress': 'In Progress',
    'modal.completed': 'Completed',
    'modal.requested_by': 'Requested by',
    'modal.profile': 'Profile',
    'modal.change_photo': 'Change photo',
    'modal.uploading': 'Uploading...',
    'modal.plan': 'Plan',
    'modal.team': 'Team',
    'modal.change_password': 'Change password',
    'modal.current_password': 'Current password',
    'modal.new_password': 'New password',
    'modal.confirm_password': 'Confirm new password',
    'modal.update_password': 'Update password',
    'modal.password_updated': 'Password updated',
    'modal.professional_plan': 'Professional Plan',
    'modal.full_access': 'Full access to all features',
    'modal.status': 'Status',
    'modal.active': 'Active',
  },
  fr: {
    // Navigation
    'nav.overview': 'Vue d\'ensemble',
    'nav.navigation': 'NAVIGATION',
    'nav.cb_universe': 'Profil d\'OC',
    'nav.aggregations': 'Agrégations',
    'nav.portfolio': 'Portefeuille',
    'nav.favorites': 'Favoris',
    'nav.simulations': 'Simulations',
    'nav.cb_report': 'Rapport CB',
    'nav.market_report': 'Rapport de marché',
    'nav.performance': 'Performance',
    'nav.performance_simulation': 'Simulation de performance',
    'nav.admin': 'Admin',
    'nav.admin_teams': 'Équipes',
    'nav.admin_users': 'Utilisateurs',
    'nav.admin_notifications': 'Notifications',
    'nav.admin_data': 'Données',
    'nav.my_data': 'Mes données',
    'admin.client_code': 'Code client',
    'admin.include_shared_data': 'Inclure les données partagées',
    'admin.edit_team': 'Modifier l\'équipe',
    'admin.edit_user': 'Modifier l\'utilisateur',
    'admin.edit': 'Modifier',
    'admin.save': 'Enregistrer',
    'admin.saving': 'Enregistrement…',
    'admin.shared_data': 'Données partagées',
    'admin.shared_and_private': 'Partagées + privées',
    'admin.private_only': 'Privées uniquement',
    'admin.no_client_code': 'Pas de code client',
    'admin.shared_data_enabled_hint': 'Cette équipe voit les données de marché partagées (globales) en plus de ses données privées.',
    'admin.shared_data_disabled_hint': 'Cette équipe ne voit que ses données client privées. Activez pour lui donner aussi accès aux données partagées.',
    'admin.assign_to_team': 'Assigner à l\'équipe',
    'admin.remove_user_from_team': 'Retirer de l\'équipe',
    'admin.remove': 'Retirer',
    'nav.admin_leads': 'Leads',
    'nav.admin_plans': 'Plans',
    'nav.admin_ai': 'Instructions IA',
    'nav.admin_reviews': 'Revues analystes',
    'nav.analyst_reviews': 'Demandes de revue',
    'nav.logout': 'Déconnexion',

    'admin.title': 'Admin',
    'admin.description': 'Gérer les équipes, utilisateurs et exports',
    'admin.teams': 'Équipes',
    'admin.users': 'Utilisateurs',
    'admin.data_import': 'Import des données',
    'admin.import_cbhist': 'Importer cbhist (JSON)',
    'admin.import_cbhist_hint': 'Envoi de cbhist.json pour remplacer les données quotidiennes. Les obligations doivent exister en base.',
    'admin.create_team': 'Créer une équipe',
    'admin.create_user': 'Créer un utilisateur',
    'admin.team_name': 'Nom de l\'équipe',
    'admin.team_description': 'Description',
    'admin.user_email': 'E-mail',
    'admin.user_name': 'Nom',
    'admin.user_role': 'Rôle',
    'admin.user_team': 'Équipe',
    'admin.user_active': 'Actif',
    'admin.no_team': '—',
    'admin.login_as': 'Se connecter comme cet utilisateur',
    'admin.back_to_admin': 'Retour à l\'admin',
    'admin.send_notification': 'Envoyer une notification',
    'admin.notifications_tab': 'Notifications',
    'admin.to_all': 'Tous les utilisateurs',
    'admin.to_user': 'À l\'utilisateur',
    'admin.team_users': 'Utilisateurs dans l\'équipe',
    'admin.loading_teams': 'Chargement des équipes...',
    'admin.loading_users': 'Chargement des utilisateurs...',
    'admin.no_users_in_team': 'Aucun utilisateur dans cette équipe.',
    'admin.close': 'Fermer',
    'admin.notification_title': 'Titre de la notification',
    'admin.message_optional': 'Message (optionnel)',
    'admin.password': 'Mot de passe',
    'admin.viewer': 'Observateur',
    'admin.analyst': 'Analyste',
    'admin.delete_team_confirm': 'Supprimer cette équipe ?',
    'admin.imported_count': 'Importés',
    'admin.skipped_count': 'Ignorés (obligation non trouvée)',
    'admin.importing': 'Import en cours...',
    'admin.import': 'Importer',
    'admin.notification_sent': 'Notification(s) envoyée(s).',
    'admin.delete': 'Supprimer',
    // Plans
    'admin.plans': 'Plans',
    'admin.plans_title': 'Plans',
    'admin.create_plan': 'Créer un plan',
    'admin.plan_name': 'Nom du plan',
    'admin.plan_description': 'Description',
    'admin.plan_price': 'Prix',
    'admin.plan_period_days': 'Jours',
    'admin.allowed_tabs': 'Onglets autorisés',
    'admin.assign_plan': 'Assigner',
    'admin.assign_plan_to_user': 'Assigner un plan à un utilisateur',
    'admin.select_user': 'Choisir un utilisateur',
    'admin.select_plan': 'Choisir un plan',
    'admin.existing_plans': 'Plans existants',
    'admin.loading': 'Chargement...',
    'admin.loading_plans': 'Chargement...',
    'admin.plan_assigned': 'Plan assigné !',
    'admin.price_period_tabs': 'Prix : {price} € | Période : {days} jours | Onglets : {tabs}',
    // AI Instructions
    'admin.ai_instructions': 'Instructions IA',
    'admin.ai_instructions_title': 'Instructions IA',
    'admin.set_ai_instruction': 'Définir l\'instruction IA par page',
    'admin.ai_instruction_hint': 'Définir les instructions de comportement de l\'assistant IA sur chaque page du tableau de bord.',
    'admin.save_instruction': 'Enregistrer l\'instruction',
    'admin.current_instructions': 'Instructions actuelles',
    'admin.no_instructions': 'Aucune instruction définie.',
    'admin.instruction_saved': 'Instruction enregistrée !',
    'admin.ai_placeholder': 'Instructions IA pour la page {page}...',
    'admin.updated': 'Mis à jour',
    'admin.edit_instruction': 'Modifier',
    'admin.delete_instruction': 'Supprimer',
    'admin.instruction_deleted': 'Instruction supprimée.',
    'admin.delete_instruction_confirm': 'Supprimer cette instruction IA ?',
    // Analyst Reviews
    'admin.analyst_reviews': 'Revues analystes',
    'admin.reviews_title': 'Toutes les revues analystes',
    'admin.no_reviews_yet': 'Aucune revue pour l\'instant.',
    'admin.loading_reviews': 'Chargement...',
    'admin.select_analyst': 'Choisir un analyste',
    'admin.assign': 'Assigner',
    'admin.assigning': 'Assignation...',
    'admin.user': 'Utilisateur',
    'admin.analyst_label': 'Analyste',
    'admin.page': 'Page',
    'admin.unassigned': 'Non assigné',
    // Data (admin)
    'admin.data_title': 'Données',
    'admin.data_description': 'Voir et modifier les données',
    'walkthrough.admin_data_label': 'Données',
    'walkthrough.admin_data_description': 'Voir et modifier les données quotidiennes, obligations, projections et émetteurs. Créer, modifier ou supprimer des lignes dans chaque onglet.',
    'walkthrough.leads_label': 'Leads',
    'walkthrough.leads_description': 'Voir les inscriptions newsletter et demandes de démo depuis la page d\'accueil. Exporter ou gérer les leads marketing.',
    'walkthrough.overview_label': 'Vue d\'ensemble',
    'walkthrough.overview_description': 'Votre tableau de bord principal avec des indicateurs en temps réel sur le marché des obligations convertibles, indicateurs clés et métriques.',
    'walkthrough.universe_label': 'Univers des OC',
    'walkthrough.universe_description': 'Parcourir et filtrer l\'univers des obligations convertibles avec données détaillées et outils de screening.',
    'walkthrough.aggregations_label': 'Agrégations',
    'walkthrough.aggregations_description': 'Filtrer et agréger les données de marché par secteur, notation et émetteur.',
    'walkthrough.performance_label': 'Performance',
    'walkthrough.performance_description': 'Attribution de performance et analyses pour votre portefeuille et les obligations sélectionnées.',
    'walkthrough.teams_label': 'Équipes',
    'walkthrough.teams_description': 'Créer et gérer les équipes. Voir les utilisateurs par équipe et supprimer les équipes inutiles.',
    'walkthrough.users_label': 'Utilisateurs',
    'walkthrough.users_description': 'Créer des utilisateurs, définir leur rôle (lecteur, analyste, admin) et équipe. Se connecter en tant qu\'utilisateur pour voir l\'app comme eux.',
    'walkthrough.notifications_label': 'Notifications',
    'walkthrough.notifications_description': 'Envoyer des notifications à tous les utilisateurs ou à un utilisateur. Pour annonces ou alertes.',
    'walkthrough.plans_label': 'Plans',
    'walkthrough.plans_description': 'Créer des plans d\'abonnement (nom, prix, période, onglets autorisés). Assigner des plans aux utilisateurs pour contrôler l\'accès.',
    'walkthrough.ai_label': 'Instructions IA',
    'walkthrough.ai_description': 'Définir les instructions par page pour l\'assistant IA (Vue d\'ensemble, Univers, Agrégations, Performance).',
    'walkthrough.reviews_label': 'Revues analystes',
    'walkthrough.reviews_description': 'Voir toutes les demandes de revue. Assigner les revues en attente aux analystes pour qu\'ils rédigent l\'analyse.',
    'walkthrough.skip': 'Passer',
    'walkthrough.back': 'Précédent',
    'walkthrough.next': 'Suivant',
    'walkthrough.done': 'Terminer',
    'admin.data_tab_daily': 'Données quotidiennes',
    'admin.data_tab_bonds': 'Obligations',
    'admin.data_tab_projections': 'Projections',
    'admin.data_tab_issuers': 'Émetteurs',
    'admin.data_no_rows': 'Aucune ligne.',
    'admin.data_bond_name': 'Obligation',
    'admin.data_bond_code': 'Code obligation',
    'admin.data_search': 'Rechercher',
    'admin.data_apply_filters': 'Appliquer',
    'admin.data_clear_filters': 'Effacer',
    'admin.data_date': 'Date',
    'admin.data_issuer_name': 'Émetteur',
    'admin.data_currency': 'Devise',
    'admin.data_maturity': 'Échéance',
    'admin.data_ex_date': 'Date ex',
    'admin.data_country': 'Pays',
    'admin.data_issuer_sector': 'Secteur émetteur',
    'admin.data_delete_confirm': 'Supprimer cette ligne ?',
    'admin.data_showing': 'Affichage',
    'admin.data_of': 'sur',
    'admin.data_prev': 'Précédent',
    'admin.data_next': 'Suivant',
    'admin.data_page': 'Page',
    'admin.data_per_page': 'Par page',
    'admin.data_create': 'Créer',
    'admin.data_required_fields': 'Veuillez remplir les champs obligatoires.',
    'admin.data_bulk_import': 'Import en masse',
    'admin.data_bulk_delete': 'Suppression en masse',
    'admin.data_bulk_delete_count': 'Supprimer ({count})',
    'admin.data_create_daily': 'Créer des données quotidiennes',
    'admin.data_create_bond': 'Créer une obligation',
    'admin.data_create_projection': 'Créer une projection de dividende',
    'admin.data_create_issuer': 'Créer un émetteur',
    'admin.data_import_daily': 'Import en masse — Données quotidiennes (Excel / CSV)',
    'admin.data_import_bonds': 'Import en masse — Obligations (Excel / CSV)',
    'admin.data_import_projections': 'Import en masse — Projections (Excel / CSV)',
    'admin.data_import_issuers': 'Import en masse — Émetteurs (Excel / CSV)',
    'admin.data_excel_format': 'Format Excel ou CSV — la première ligne doit contenir les en-têtes. Colonnes requises :',
    'admin.data_excel_optional': 'Colonnes optionnelles :',
    'admin.data_choose_excel': 'Choisir un fichier Excel ou CSV',
    'admin.data_download_excel_template': 'Télécharger le modèle Excel',
    'admin.data_download_csv_template': 'Télécharger le modèle CSV',
    'admin.data_download_excel': 'Télécharger Excel',
    'admin.data_download_csv': 'Télécharger CSV',
    'admin.data_exporting': 'Export en cours…',
    'admin.data_export_empty': 'Aucune donnée à exporter pour les filtres actuels.',
    'admin.data_importing': 'Import en cours…',
    'admin.data_creating': 'Création…',
    'admin.data_save_changes': 'Enregistrer',
    'admin.data_clear': 'Effacer',
    'admin.data_view': 'Voir',
    'admin.data_del': 'Suppr.',
    'admin.data_rows': 'lignes',
    'admin.data_bonds_count': 'obligations',
    'admin.data_projections_count': 'projections',
    'admin.data_issuers_count': 'émetteurs',
    'admin.data_bond_code_required': 'Code obligation et date requis',
    'admin.data_no_valid_rows_daily': 'Aucune ligne valide. Vérifiez les colonnes bondCode et date.',
    'admin.data_select_one_row': 'Sélectionnez au moins une ligne',
    'admin.data_select_one_bond': 'Sélectionnez au moins une obligation',
    'admin.data_select_one_projection': 'Sélectionnez au moins une projection',
    'admin.data_select_one_issuer': 'Sélectionnez au moins un émetteur',
    'admin.data_delete_daily_confirm': 'Supprimer {count} ligne(s) sélectionnée(s) ? Cette action est irréversible.',
    'admin.data_delete_bonds_confirm': 'Supprimer {count} obligation(s) sélectionnée(s) et leurs données quotidiennes ? Cette action est irréversible.',
    'admin.data_delete_projections_confirm': 'Supprimer {count} projection(s) sélectionnée(s) ?',
    'admin.data_delete_issuers_confirm': 'Supprimer {count} émetteur(s) sélectionné(s) ? Les obligations les référençant devront être mises à jour.',
    'admin.data_deleted_count': '{count} lignes supprimées',
    'admin.data_deleted_bonds': '{count} obligations supprimées',
    'admin.data_deleted_projections': '{count} projections supprimées',
    'admin.data_deleted_issuers': '{count} émetteurs supprimés',
    'admin.data_imported_count': '{count} lignes importées',
    'admin.data_imported_bonds': '{count} obligations importées',
    'admin.data_imported_projections': '{count} projections importées',
    'admin.data_imported_issuers': '{count} émetteurs importés',
    'admin.data_no_valid_rows_bonds': 'Aucune ligne valide. Vérifiez que toutes les colonnes requises existent.',
    'admin.data_no_valid_rows_projections': 'Aucune ligne valide. Vérifiez les colonnes bondCode, exDate et amount.',
    'admin.data_no_valid_rows_issuers': 'Aucune ligne valide. Vérifiez les colonnes name et country.',
    'admin.data_bond_exdate_amount_required': 'Code obligation, date ex et montant requis',
    'admin.data_name_country_required': 'Nom et pays requis',
    'admin.leads_title': 'Leads',
    'admin.leads_description': 'Inscriptions à la newsletter et demandes de démo depuis la page d\'accueil',
    'admin.leads_tab_newsletter': 'Inscriptions newsletter',
    'admin.leads_tab_demo': 'Demandes de démo',
    'admin.leads_newsletter_count': 'inscriptions',
    'admin.leads_demo_count': 'demandes',
    'admin.leads_email': 'E-mail',
    'admin.leads_signed_up_at': 'Inscrit le',
    'admin.leads_name': 'Nom',
    'admin.leads_company': 'Société',
    'admin.leads_phone': 'Téléphone',
    'admin.leads_requested_at': 'Demandé le',
    'admin.edit': 'Modifier',
    'admin.save': 'Enregistrer',
    'admin.cancel': 'Annuler',
    'admin.actions': 'Actions',

    // Account
    'account.messages': 'Messages',
    'account.settings': 'Paramètres',
    'account.help': 'Aide',

    // Login
    'login.enter_details': 'Veuillez saisir vos identifiants.',
    'login.email': 'E-mail',
    'login.email_placeholder': 'Saisir votre e-mail',
    'login.password': 'Mot de passe',
    'login.log_in': 'Connexion',
    'login.signing_in': 'Connexion...',
    'login.invalid_credentials': 'E-mail ou mot de passe incorrect',
    'login.error_generic': 'Une erreur s\'est produite. Veuillez réessayer.',

    'errors.network_issue': 'Problème de connexion. Vérifiez votre accès à Internet et réessayez.',
    'errors.generic': 'Une erreur s\'est produite. Veuillez réessayer.',

    // Dashboard Pages
    'dashboard.overview': 'Aperçu du tableau de bord',
    'dashboard.overview_desc': 'Informations en temps réel sur le marché des obligations convertibles',
    'dashboard.analyst_desc': 'Gérer les revues assignées, voir en tant que demandeur et soumettre les rapports d\'analyse',
    'dashboard.universe': 'Univers des obligations convertibles',
    'dashboard.universe_desc': 'obligations',
    'dashboard.aggregations': 'Agrégations',
    'dashboard.aggregations_desc': 'Analyser le marché à travers plusieurs dimensions',
    'dashboard.portfolio': 'Analyse de portefeuille',
    'dashboard.portfolio_desc': 'obligations sélectionnées • Capitalisation totale:',
    'dashboard.instrument_desc': 'ISIN:',
    
    // KPIs
    'kpi.total_convertible_bonds': 'Total obligations convertibles',
    'kpi.total_market_cap': 'Market Value',
    'kpi.average_yield': 'YTM',
    'kpi.average_1d_change': '1D Change',
    'kpi.ytm': 'YTM',
    'kpi.vs_yesterday': 'vs yesterday',
    'kpi.price': 'Price',
    'kpi.fair_value': 'Fair Value',
    'kpi.ytm_yield': 'YTM (Yield)',
    'kpi.coupon': 'Coupon',
    'kpi.conversion_price': 'Conversion Price',
    'kpi.parity': 'Parity',
    'kpi.delta': 'Delta',
    'kpi.theta': 'Theta',
    'kpi.total_bonds': 'Total Bonds',
    'kpi.weighted_delta': 'Weighted Delta',
    'kpi.weighted_ytm': 'Weighted YTM',
    'kpi.1m_performance': '1M Performance',
    'kpi.avg_performance_1m': 'Avg Performance 1M',
    'kpi.performance_1m': 'Performance 1M',
    'kpi.portfolio_value': 'Market Value',
    'kpi.daily_pnl': 'Daily P&L',
    'kpi.portfolio_delta': 'Delta',
    'kpi.avg_ytm': 'YTM',
    'kpi.avg_implied_vol': 'Implied Vol',
    'kpi.portfolio_vega': 'Vega',
    'kpi.portfolio_gamma': 'Gamma',
    'kpi.avg_prime': 'Premium',
    'kpi.avg_duration': 'Duration',
    'kpi.avg_credit_spread': 'Credit Spread',
    'kpi.subtitle_eur': 'EUR',
    'kpi.subtitle_vs_yesterday': 'vs yesterday',
    'kpi.subtitle_equity_exposure': 'Equity Exposure',
    'kpi.subtitle_yield_to_maturity': 'Yield to Maturity',
    'kpi.subtitle_balanced_profile': 'Balanced Profile',
    'kpi.subtitle_vol_sensitivity': 'Vol Sensitivity',
    'kpi.subtitle_convexity': 'Convexity',
    'kpi.subtitle_conversion_premium': 'Conversion Premium',
    'kpi.subtitle_interest_rate_risk': 'Interest Rate Risk',
    'kpi.subtitle_bond_profile': 'Bond Profile',
    
    // Charts & Widgets
    'widget.market_index_performance': 'Performance des indices de marché',
    'widget.sector_breakdown': 'Répartition sectorielle',
    'widget.credit_rating_breakdown': 'Répartition des notations',
    'widget.maturity_breakdown': 'Répartition des échéances',
    'widget.profile_breakdown': 'Répartition des profils',
    'widget.performance_history': 'Historique des performances (30 jours)',
    'widget.sector_allocation': 'Allocation sectorielle',
    'widget.rating_distribution': 'Distribution des notations',
    'widget.credit_rating_distribution': 'Distribution des notations de crédit',
    'widget.historical_prices': 'Prix historiques et grecques',
    'widget.bond_characteristics': 'Caractéristiques de l\'obligation',
    'widget.last_30_days': 'Tendance des performances des 30 derniers jours',
    'widget.top_worst_performers': 'Meilleurs & pires performances',
    'widget.top_3': 'Top 3',
    'widget.worst_3': 'Pires 3',
    'widget.cb_performance': 'Performance CB',
    'widget.equity_performance': 'Performance Actions',
    'widget.country_distribution': 'Distribution par Pays',
    'widget.cb_strike_analysis': 'Analyse CB Strike vs Prix CB',
    'widget.cb_strike_description': 'Relation entre le CB Strike et le prix de l\'obligation convertible, coloré par profil CB',
    'widget.inflows_outflows': 'Entrées / Sorties',
    'widget.inflows': 'Entrées',
    'widget.outflows': 'Sorties',
    'flows.added': 'Ajouté',
    'flows.removed': 'Retiré',

    // Legend items
    'legend.cb_index': 'Indice CB',
    'legend.equity_index': 'Indice actions',
    'legend.delta_neutral': 'Delta neutre',
    
    // Table Headers
    'table.isin': 'ISIN',
    'table.issuer': 'Émetteur',
    'table.sector': 'Secteur',
    'table.currency': 'Devise',
    'table.coupon': 'Coupon',
    'table.price': 'Prix',
    'table.delta': 'Delta',
    'table.rating': 'Notation',
    'table.maturity': 'Échéance',
    'table.perf_1m': 'Perf 1M',
    
    // Buttons
    'button.export_csv': 'Exporter en CSV',
    'button.back_to_universe': 'Retour à l\'univers',
    'button.save_inputs': 'Enregistrer les entrées personnalisées',
    
    // Filter Labels
    'filter.search': 'Rechercher par ISIN, émetteur ou pays...',
    'filter.sector': 'Secteur',
    'filter.country': 'Pays',
    'filter.rating': 'Notation de crédit',
    'filter.currency': 'Devise',
    
    // Dimension Labels
    'dimension.primary': 'Dimension primaire',
    'dimension.secondary': 'Dimension secondaire',
    'dimension.secondary_cross_filter': 'Dimension secondaire (Filtre croisé)',
    'dimension.sector': 'Secteur',
    'dimension.rating': 'Notation de crédit',
    'dimension.size': 'Taille de capitalisation',
    'dimension.profile': 'Profil d\'obligation',
    'dimension.maturity': 'Tranche d\'échéance',
    
    // Aggregations Page
    'aggregations.universe_weighted_greeks': 'Greeks',
    'aggregations.valuation_metrics': 'Métriques de valorisation',
    'aggregations.premium_adjusted': 'Prime / Prime ajustée',
    'aggregations.yield_to_maturity': 'Rendement à l\'échéance',
    'aggregations.implied_historical_vol': 'Vol implicite vs Vol historique (VI & VH)',
    'aggregations.credit_spread': 'Spread de crédit & Spread implicite',
    'aggregations.historical_distribution': 'Distribution historique',
    'aggregations.breakdown_by': 'Répartition par',
    'aggregations.detailed_breakdown': 'Répartition détaillée',
    'aggregations.summary_table': 'Tableau récapitulatif',
    'aggregations.cross_filter_analysis': 'Analyse de filtres croisés',
    'aggregations.cb_market_cap': 'Market Value OC',
    'aggregations.equity_market_cap': 'Market Value Actions',
    'aggregations.avg_prime': 'Premium %',
    'aggregations.avg_parity': 'Parity %',
    'aggregations.avg_ytm': 'YTM',
    'aggregations.bond_floor': 'Bondfloor %',
    'aggregations.no_cross_filter': 'Pas de filtre croisé disponible pour',
    'aggregations.showing': 'Affichage',
    'aggregations.breakdown_with_cross_filter': 'répartition avec filtre croisé par',
    'aggregations.count': 'Nombre',
    'aggregations.market_cap': 'Market Value',
    'aggregations.avg_delta': 'Delta',
    'aggregations.current_distribution': 'Distribution actuelle:',
    
    // Performance Attribution Page
    'performance.title': 'Performance & Attribution',
    'performance.description': 'Analyse de performance',
    'performance.select_period': 'Sélectionner la période',
    'performance.export_data': 'Exporter les données',
    'performance.period': 'Période',
    'performance.universe_performance': 'Performance de l\'univers',
    'performance.cb_performance': 'Performance OC',
    'performance.equity_performance': 'Performance Actions',
    'performance.delta_neutral': 'Delta Neutre',
    'performance.cb_pnl': 'P&L Obligation Convertible',
    'performance.equity_contribution': 'Contribution des actions sous-jacentes',
    'performance.hedged_performance': 'Performance couverte',
    'performance.performance_trend': 'Tendance de performance',
    'performance.attribution': 'Attribution de performance',
    'performance.attribution_description': 'Répartition par source de contribution: Action, Crédit, Portage, Taux, Valorisation, FX',
    'performance.total': 'Total',
    'performance.share': 'Contribution Actions',
    'performance.credit_spread': 'Spread de crédit',
    'performance.carry': 'Portage',
    'performance.rate': 'Taux',
    'performance.valuation': 'Valorisation',
    'performance.fx': 'FX',
    'performance.contribution': 'Contribution',
    'performance.by_filter': 'Performance par filtre',
    'performance.weight': 'Poids',
    'performance.delta_gamma': 'Equity',
    'performance.credit': 'Spread',
    'performance.spread': 'Spread',
    'performance.total_contrib': 'Contrib Total',
    'performance.perf_abs': 'Perf Abs',
    'performance.perf_abs_cb': 'Perf Abs OC',
    'performance.perf_abs_equity': 'Perf Abs Equity',
    'performance.equity_perf_abs': 'Perf Equity Abs (cb)',
    'performance.by': 'par',
    'performance.bubble_description': 'Chaque bulle représente une catégorie. Axe X: Performance OC (Abs), Axe Y: Performance Actions (Abs).',
    'performance.top_worst_contributors': 'Meilleurs & pires contributeurs',
    'performance.top_10': 'Top 10 contributeurs',
    'performance.worst_10': 'Pires 10 contributeurs',
    'performance.absolute_performance': 'Perf Abs',
    'performance.cb_vs_equity': 'Performance OC vs Actions',
    'performance.scatter_description': 'Chaque point représente une obligation convertible. Axe X: Contribution actions, Axe Y: Performance OC. Taille: Montant en circulation.',
    'performance.equity_contrib': 'Contribution Actions (%)',
    'performance.cb_perf': 'Performance OC (%)',
    'performance.from': 'De',
    'performance.to': 'À',
    'performance.select_start': 'Sélectionner date de début',
    'performance.select_end': 'Sélectionner date de fin',
    
    // Instrument Page
    'instrument.back_to_universe': 'Retour à l\'univers',
    'instrument.performance_overview': 'Aperçu des performances',
    'instrument.latest_data': 'Dernières données',
    'instrument.live_values': 'Valeurs en direct',
    'instrument.cb_price': 'Prix OC',
    'instrument.stock_price': 'Prix de l\'action',
    'instrument.spread_input': 'Spread (Entrée)',
    'instrument.implied_spread': 'Spread implicite',
    'instrument.implied_vol': 'VI (Vol implicite)',
    'instrument.historical_vol': 'VH (Vol historique)',
    'instrument.volatility_analysis': 'Analyse de volatilité',
    'instrument.credit_analysis': 'Analyse de crédit',
    'instrument.vega_high': '(Élevé)',
    'instrument.vega_low': '(Faible)',
    'instrument.vol_spread': 'Spread Vol (VI - VH)',
    'instrument.avg_spread_vol': 'Spread Vol moyen',
    'instrument.std_deviation': 'Écart type',
    'instrument.spread_to_average': 'Spread par rapport à la moyenne',
    'instrument.zscore': 'Z-Score',
    'instrument.downside_risk': 'Risque de baisse',
    'instrument.spread_differential': 'Différentiel de spread',
    'instrument.current_yield': 'Rendement actuel',
    'instrument.credit_sensitivity': 'Sensibilité au crédit',
    'instrument.relative_valuation': 'Valorisation relative',
    'instrument.static_information': 'Informations statiques',
    'instrument.issue_date': 'Date d\'émission',
    'instrument.conversion_ratio': 'Ratio de conversion',
    'instrument.underlying': 'Sous-jacent',
    'instrument.outstanding_amount': 'Montant en circulation',
    'instrument.richness_cheapness': 'Résumé Richesse / Bon marché',
    'instrument.observation': 'Observation',
    'instrument.financial_indicators': 'Indicateurs financiers',
    'instrument.historical_analysis': 'Analyse historique',
    'instrument.cb_vs_underlying': 'Prix OC vs Sous-jacent',
    'instrument.custom_inputs': 'Entrées personnalisées',
    'instrument.custom_inputs_description': 'Remplacer les valeurs par défaut pour une analyse personnalisée. Les valeurs sont enregistrées localement.',
    'instrument.save_custom': 'Enregistrer entrées personnalisées',
    'instrument.repo': 'Repo (%)',
    'instrument.underpriced': 'Sous-évalué',
    'instrument.overpriced': 'Surévalué',
    'instrument.fair': 'Juste',
    
    // Universe Page
    'universe.select_bond': 'Sélectionner une obligation convertible',
    'universe.select_placeholder': 'Rechercher par émetteur ou ISIN...',
    'universe.chart_category': 'Catégorie de graphique',
    'universe.chart_type': 'Type de graphique',
    'universe.performance_period': 'Période de performance',
    'universe.key_metrics': 'Métriques clés',
    'universe.performance': 'Performance',
    'universe.greeks': 'Grecques',
    'universe.pricing_valuation': 'Prix & Valorisation',
    'universe.valuation_metrics': 'Métriques de valorisation',
    'universe.days': 'jours',
    'universe.actual_days': 'Jours réels',
    'universe.custom_inputs': 'Entrées personnalisées',
    'universe.cb_price': 'Prix CB',
    'universe.volatility': 'Volatilité',
    'universe.stock': 'Action',
    'universe.apply': 'Appliquer',
    'universe.reset': 'Réinitialiser',
    'universe.issue_characteristics': 'Caractéristiques de l\'émission',
    'universe.nominal': 'Nominal',
    'universe.conversion_ratio': 'Ratio de conversion',
    'universe.conversion_price': 'Prix de conversion',
    'universe.issue_price': 'Prix d\'émission',
    'universe.coupon': 'Coupon',
    'universe.issue_date': 'Date d\'émission',
    'universe.maturity_date': 'Date d\'échéance',
    'universe.amount_issued': 'Montant émis',
    'universe.currency': 'Devise',
    'universe.rating': 'Notation',
    'universe.underlying': 'Sous-jacent',
    'universe.performance_attribution': 'Attribution de Performance',
    'universe.start_date': 'Date Début',
    'universe.end_date': 'Date Fin',
    'universe.share_contrib': 'Contrib. Action',
    'universe.credit_spread_contrib': 'Contrib. Spread Crédit',
    'universe.carry_contrib': 'Contrib. Carry',
    'universe.rate_contrib': 'Contrib. Taux',
    'universe.valuation': 'Valorisation',
    'universe.fx_contrib': 'Contrib. FX',
    'universe.select_date_range': 'Sélectionnez une plage de dates pour voir l\'attribution',
    'universe.ratchet_takeover': 'Clause Ratchet & Prime OPA',
    'universe.change_of_control': 'Change de Contrôle',
    'universe.takeover_simulation': 'Simulation OPA',
    'universe.takeover_premium': 'Prime OPA (%)',
    'universe.bid_date': 'Date Offre',
    'universe.results': 'Résultats',
    'universe.current_cp': 'CP Actuel',
    'universe.new_cp': 'Nouveau CP',
    'universe.current_cr': 'CR Actuel',
    'universe.new_cr': 'Nouveau CR',
    'universe.bond_performance': 'Performance Obligation',
    'universe.latest_valuation': 'Dernière Valorisation & KPIs',
    'universe.credit_metrics': 'Métriques Crédit',
    'universe.current_yield': 'Rend. courant',
    'universe.credit_sens': 'Sens. Crédit',
    'universe.volatility_sensitivity': 'Volatilité & Sensibilité',
    'universe.pricing': 'Prix',
    'universe.fair_value': 'Fair Value',
    'universe.option_value': 'Option Value',
    'universe.premium': 'Prime',
    'universe.adj_premium': 'Prime Ajustée',
    'universe.parity': 'Parité',
    'universe.historical_analysis': 'Analyse Historique',
    'universe.premium_percent': 'Prime %',
    'universe.adj_premium_percent': 'Prime Ajustée %',
    'universe.implied_spread_bp': 'Spread Implicite (bp)',
    'universe.credit_spread_bp': 'Spread Crédit (bp)',
    
    // Common
    'common.issuer': 'Émetteur',
    'common.sector': 'Secteur',
    'common.country': 'Pays',
    'common.rating': 'Notation',
    'common.maturity': 'Échéance',
    'common.type': 'Type',
    'common.profile': 'Profil',
    'common.size': 'Taille',
    'common.bond_not_found': 'Obligation non trouvée',
    'common.no_data': 'Aucune donnée disponible',
    'common.loading': 'Chargement...',
    'common.percentage': '%',
    'common.basis_points': 'bp',
    
    // Other
    'currency.us_dollar': 'EUR',
    'search.placeholder': 'Rechercher par ISIN, émetteur ou pays...',
    'portfolio.select_bonds': 'Sélectionner les obligations à inclure dans votre analyse de portefeuille',
    'chart.primary_dimension': 'Graphique de dimension primaire',
    'chart.secondary_dimension': 'Graphique de dimension secondaire',
    'dark_mode': 'Mode sombre',
    'light_mode': 'Mode clair',
    'of': 'de',
    'tracking': 'Suivi',
    
    // Signals Analysis
    'signals.impliedVolatilityAnalysis': 'Analyse de la volatilité implicite',
    'signals.impliedSpreadAnalysis': 'Analyse du spread implicite',
    'signals.creditSpreadGapAnalysis': 'Analyse de l\'écart de spread (crédit)',
    'signals.lookbackPeriod': 'Période de référence',
    'signals.impliedVol': 'Vol implicite',
    'signals.ivVariation': 'Variation VI',
    'signals.actualDays': 'Jours réels',
    'signals.creditSpread': 'Spread crédit',
    'signals.impliedSpread': 'Spread implicite',
    'signals.spreadVariation': 'Var Spread',
    'signals.valuation': 'Valorisation',
    'signals.noCBsMeetingCriteria': 'Aucune OC ne correspond aux critères pour cette période',
    'signals.overpriced': 'Surévalué',
    'signals.underpriced': 'Sous-évalué',
    'signals.expensive': 'Cher',
    'signals.fair_value': 'Juste valeur',
    'signals.relative_situation': 'Situation relative',
    
    // Overview Page Tabs
    'tabs.overview': 'Vue d\'ensemble',
    'tabs.performance': 'Performance',
    'tabs.signals_analysis': 'Analyse des signaux',
    
    // Performance Cards
    'performance.universe_1d': 'Performance Univers (1J)',
    'performance.equity_1d': 'Performance Actions (1J)',
    'performance.delta_neutral_1d': 'Performance Delta-Neutre (1J)',
    
    // Size Breakdown
    'widget.size_breakdown': 'Répartition par taille (par capitalisation boursière des actions)',
    
    // Size Labels
    'size.large_cap': 'Grande capitalisation',
    'size.mid_cap': 'Capitalisation moyenne',
    'size.small_cap': 'Petite capitalisation',
    
    // Profile Labels
    'profile.balanced': 'Mixte',
    'profile.bond': 'Obligation',
    'profile.equity': 'Action',
    'profile.high_yield': 'High Yield',
    'profile.distressed': 'Distressed',
    
    // Period Labels
    'period.1d': '1J',
    'period.mtd': 'MTD',
    'period.3mtd': '3MTD',
    'period.ytd': 'YTD',
    'period.vol_sensitive_bound': 'Borne Vol-Sensible',
    
    // Implied Volatility Widget
    'widget.implied_volatility_variation': 'Variation de volatilité implicite – Régime Vol-Sensible',
    'widget.top_5_implied_vol': 'Top 5 Vol Implicite ↑',
    'widget.flop_5_implied_vol': 'Flop 5 Vol Implicite ↓',
    
    // Implied Spread Widget
    'widget.implied_spread_variation': 'Variation de spread implicite – Univers Crédit-Sensible',
    'widget.top_5_spread_widening': 'Top 5 Élargissement Spread ↑',
    'widget.bottom_5_spread_tightening': 'Bottom 5 Resserrement Spread ↓',
    
    // Table Headers for Rankings
    'table.delta_iv': 'ΔVI',
    'table.delta_spr': 'ΔSpr',
    'table.vega': 'Vega',
    'table.profile': 'Profil',

    // AI Assistant
    'ai.disclaimer': 'L\'IA n\'est pas parfaite et peut commettre des erreurs. Ceci est un outil d\'analyse uniquement — pas un conseil en investissement.',
    'ai.assistant_title': 'Assistant ConvPilot',
    'ai.online': 'En ligne',
    'ai.live_data': 'Données en direct',
    'ai.placeholder': 'Posez des questions sur votre portefeuille, obligations, métriques...',
    'ai.welcome': 'Bonjour ! Je suis votre assistant ConvPilot. J\'ai accès aux données de votre tableau de bord. Posez-moi des questions sur votre portefeuille, les métriques obligataires ou l\'analyse de marché !',
    'ai.thinking': 'Réflexion...',
    'ai.error': 'Désolé, une erreur s\'est produite. Veuillez réessayer.',

    // Header & modals
    'header.profile': 'Profil',
    'header.logout': 'Déconnexion',
    'header.back_to_analyst': 'Retour à l\'analyste',
    'header.user': 'Utilisateur',
    'modal.close': 'Fermer',
    'modal.cancel': 'Annuler',
    'modal.logout_confirm_title': 'Déconnexion ?',
    'modal.logout_confirm_message': 'Êtes-vous sûr de vouloir vous déconnecter ?',
    'modal.analyst_reviews': 'Revues analyste',
    'modal.request_analyst_review': 'Demander une revue analyste',
    'modal.my_reviews': 'Mes revues',
    'modal.review_requests': 'Demandes de revue',
    'modal.new_request': 'Nouvelle demande',
    'modal.write_analysis': 'Rédiger l\'analyse',
    'modal.report_title': 'Titre du rapport...',
    'modal.write_analysis_placeholder': 'Rédigez votre analyse (Markdown : **gras**, *italique*, tableaux, listes)...',
    'modal.preview_live_hint': 'L’aperçu se met à jour pendant la saisie (panneau droit).',
    'modal.submit_analysis': 'Soumettre le rapport d\'analyse',
    'modal.submitting': 'Envoi...',
    'modal.assign_to_me': 'M\'assigner',
    'modal.view_as_user': 'Voir en tant qu\'utilisateur',
    'modal.back_to_list': '← Retour à la liste',
    'modal.review_request': 'Demande de revue',
    'modal.analysis_reports': 'Rapports d\'analyse',
    'modal.no_reviews': 'Aucune revue pour l\'instant.',
    'modal.no_reviews_hint': 'Soumettez une demande pour commencer.',
    'modal.loading_reviews': 'Chargement des revues...',
    'modal.describe_requirements': 'Décrivez ce que vous souhaitez faire analyser. Soyez précis sur vos besoins et les données à analyser.',
    'modal.submit_review_request': 'Soumettre la demande de revue',
    'modal.pending': 'En attente',
    'modal.in_progress': 'En cours',
    'modal.completed': 'Terminé',
    'modal.requested_by': 'Demandé par',
    'modal.profile': 'Profil',
    'modal.change_photo': 'Changer la photo',
    'modal.uploading': 'Téléchargement...',
    'modal.plan': 'Formule',
    'modal.team': 'Équipe',
    'modal.change_password': 'Changer le mot de passe',
    'modal.current_password': 'Mot de passe actuel',
    'modal.new_password': 'Nouveau mot de passe',
    'modal.confirm_password': 'Confirmer le nouveau mot de passe',
    'modal.update_password': 'Mettre à jour le mot de passe',
    'modal.password_updated': 'Mot de passe mis à jour',
    'modal.professional_plan': 'Formule Professionnelle',
    'modal.full_access': 'Accès complet à toutes les fonctionnalités',
    'modal.status': 'Statut',
    'modal.active': 'Actif',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Load language from localStorage on initialization
    if (typeof localStorage !== 'undefined') {
      const savedLang = localStorage.getItem('convpilot-language');
      if (savedLang === 'en' || savedLang === 'fr') {
        return savedLang;
      }
    }
    return 'fr';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    // Save to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('convpilot-language', lang);
    }
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Sidebar Context
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    setIsCollapsed(prev => !prev);
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
};

// Loading Context
const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);

  const setLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <LoadingContext.Provider value={{ isLoading, setLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};

import { WalkthroughProvider } from './WalkthroughContext';
import { WalkthroughOverlay } from '../components/WalkthroughOverlay';

// Combined Context Provider
export const AppProviders = ({ children }: { children: ReactNode }) => {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <SidebarProvider>
          <WalkthroughProvider>
            <WalkthroughOverlay />
            <LoadingProvider>
              {children}
            </LoadingProvider>
          </WalkthroughProvider>
        </SidebarProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};
